import { useState, useEffect } from "react";
import { ShoppingBag, Lock, Zap, ExternalLink, Loader2, DollarSign } from "lucide-react";
import { useSubscription } from "../context/SubscriptionContext";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MARKETPLACE_TABS = [
  { id: "leboncoin", label: "Leboncoin", color: "#F56B2A" },
  { id: "vinted", label: "Vinted", color: "#09B1BA" },
  { id: "ebay", label: "eBay", color: "#E53238" },
];

function MarketplaceTab({ marketplace, email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    if (!email) { setLoading(false); return; }
    axios.get(`${API}/marketplace-products/${marketplace}`, { params: { email } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [marketplace, email]);

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin text-[#00E676]" size={24} /></div>;

  if (!data || data.locked) {
    const preview = data?.preview || [];
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-30 blur-[4px] pointer-events-none select-none">
          {preview.map((p, i) => (
            <div key={i} className="neo-card p-3 flex gap-3">
              <img src={p.image} alt="" className="w-16 h-16 object-cover border border-white/10" />
              <div><p className="font-bold text-sm">{p.name}</p><p className="text-xs text-muted-foreground">{p.price} EUR</p></div>
            </div>
          ))}
        </div>
        <div className="neo-card p-6 text-center border-[#00E676]">
          <Lock className="mx-auto mb-3 text-[#00E676]" size={32} />
          <h3 className="text-xl font-extrabold mb-1">Produits {marketplace.charAt(0).toUpperCase() + marketplace.slice(1)}</h3>
          <p className="text-sm text-muted-foreground mb-4">{data?.total_products || 8} produits a forte marge avec liens directs</p>
          <div className="flex items-baseline justify-center gap-1 mb-4">
            <span className="text-3xl font-extrabold">7</span><span className="text-lg font-bold">EUR</span>
            <span className="text-muted-foreground text-xs ml-1">paiement unique</span>
          </div>
          <button
            onClick={async () => {
              if (!email) { toast.error("Connectez-vous d'abord"); return; }
              setCheckingOut(true);
              try {
                const r = await axios.post(`${API}/marketplace-products/${marketplace}/checkout`, { email });
                window.location.href = r.data.checkout_url;
              } catch { toast.error("Erreur"); setCheckingOut(false); }
            }}
            disabled={checkingOut}
            className="neo-button-primary w-full max-w-xs mx-auto flex items-center justify-center gap-2"
            data-testid={`marketplace-${marketplace}-buy`}
          >
            {checkingOut ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            Debloquer — 7 EUR
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {data.products.map((p, i) => (
        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
          className="neo-card p-4 hover:border-[#00E676] transition-colors group block"
          data-testid={`mp-product-${marketplace}-${i}`}>
          <div className="flex gap-3">
            <img src={p.image} alt="" className="w-20 h-20 object-cover border border-white/10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm group-hover:text-[#00E676] truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground mt-1">{p.category}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs"><DollarSign size={10} className="inline" /> Acheter: <strong>{p.price} EUR</strong></span>
                <span className="text-xs text-[#00E676]">Revendre: <strong>{p.sell_price} EUR</strong></span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] font-bold border border-[#00E676]/30">+{p.margin_pct}%</span>
                <ExternalLink size={12} className="text-muted-foreground" />
              </div>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

export default function MarketplaceProducts() {
  const { email } = useSubscription();
  const [activeTab, setActiveTab] = useState("leboncoin");

  return (
    <div className="space-y-6" data-testid="marketplace-products-page">
      <div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <ShoppingBag className="text-[#00E676]" size={36} />
          PRODUITS MARKETPLACES
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-xs sm:text-sm">
          Bonnes affaires et marges elevees — Liens directs vers les produits
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {MARKETPLACE_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-5 py-2.5 text-sm font-bold uppercase border-2 transition-colors ${
              activeTab === t.id
                ? "border-[#0A0A0A] dark:border-[#00E676] bg-[#0A0A0A] text-white dark:bg-[#00E676]/10 dark:text-[#00E676]"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            style={activeTab === t.id ? { borderBottomColor: t.color } : {}}
            data-testid={`mp-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <MarketplaceTab marketplace={activeTab} email={email} />
    </div>
  );
}
